import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS } from '@repo/shared/domain';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getVerifyPermission, constructQuery } from '@repo/shared/helpers';
import { GeneralNotFound } from '@repo/components';
import { DocumentViewer } from '@repo/components';
import { Spinner } from '@repo/components';
import { getPartnerCompanyDocuments } from 'src/api/partner';
import { useBreadcrumbLevel } from '@repo/components/hooks';

export const Route = createFileRoute('/partner_/$partnerId/documents/$documentId')({
	component: RouteComponent,
});

function RouteComponent() {
	const { user: authenticatedUser, loading: userLoading } = useAuthenticatedUser();
	const { partnerId, documentId } = Route.useParams();
	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	useBreadcrumbLevel(1, 'Documents', `/partner/${partnerId}/documents`);
	useBreadcrumbLevel(2, 'Document Details');

	const query = constructQuery({ all: 'yes', includeLogs: 'true', noPagination: 'true' });
	const {
		data: documentsResponse,
		isLoading: documentsLoading,
		error: _getPartnerCompanyDocumentsError,
	} = useQuery({
		queryKey: ['partner-company-documents', partnerId, 'all'],
		queryFn: () => getPartnerCompanyDocuments(partnerId, query),
		enabled: !userLoading && !!authenticatedUser,
	});
	const document = documentsResponse?.data?.find((document) => document.id === documentId);

	if (userLoading || documentsLoading) {
		return (
			<div className="flex items-center justify-center h-full">
				<Spinner className="text-[#1E1E1E]" />
			</div>
		);
	}

	if (!document) {
		return (
			<GeneralNotFound
				label="We couldn't find this document. Maybe it has been deleted?"
				action={{
					label: 'Return to documents',
					href: `/partner/${partnerId}/documents`,
					variant: 'outlined',
				}}
			/>
		);
	}

	const handleRevalidateDocument = async () => {
		// Invalidate this specific document
		await queryClient.invalidateQueries({
			queryKey: ['partner-company-document', partnerId, documentId],
		});
	};

	const handleClose = () => {
		navigate({ to: `/partner/${partnerId}/documents` });
		return null;
	};

	return (
		<DocumentViewer
			document={document}
			onClose={handleClose}
			types={[]} // You might want to get document types from API
			apiUrlBase={`/api/partner-portal/groups/${partnerId}/files`}
			onRevalidateDocument={handleRevalidateDocument}
			tasks={[]}
			permissions={{
				metadata: verifyPermission([PERMISSIONS.partnerCompanyUsersView]),
				fullView: verifyPermission([PERMISSIONS.partnerCompanyUsersView]),
				sensitiveView: false,
				edit: false,
				archive: verifyPermission([PERMISSIONS.partnerCompanyDocumentsArchive]),
				review: false,
				sensitiveEdit: false,
				sensitiveArchive: false,
			}}
		/>
	);
}
